import React, { useEffect, useState, useMemo } from 'react';

// Material UI
import Dialog from '@material-ui/core/Dialog';
import DialogTitle from '@material-ui/core/DialogTitle';
import DialogContent from '@material-ui/core/DialogContent';
import DialogActions from '@material-ui/core/DialogActions';
import Checkbox from '@material-ui/core/Checkbox';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Typography from '@material-ui/core/Typography';
import Box from '@material-ui/core/Box';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListItemText from '@material-ui/core/ListItemText';
import ListItemSecondaryAction from '@material-ui/core/ListItemSecondaryAction';
import IconButton from '@material-ui/core/IconButton';
import CheckBoxIcon from '@material-ui/icons/CheckBox';
import CheckBoxOutlineBlankIcon from '@material-ui/icons/CheckBoxOutlineBlank';
import { PrimaryButton, SecondaryButton } from '../../_core/_ui/buttons.component';
import { useCompanySportTerminology } from '../../../lib/stores/company.store';
import { SportTerminology } from '../../../lib/types';

interface IRoleAssignmentDialog {
  open: boolean;
  onClose: () => void;
  onConfirm: (selectedUserIds: string[]) => void;
  currentAssessmentId?: string | null;
  members: IMember[];
  assessmentName: string;
}

export const RoleAssignmentDialog: React.FC<IRoleAssignmentDialog> = ({
  open,
  onClose,
  onConfirm,
  currentAssessmentId,
  members,
  assessmentName
}) => {
  const terminology = useCompanySportTerminology();
  const sessionTerm = terminology[SportTerminology.SESSION];

  // Get members who have assessment roles for the current assessment
  const eligibleMembers = useMemo(() => {
    if (!currentAssessmentId) return [];

    return members.filter(member => {
      const assessmentRoles = member.rolesByAssessment?.find(
        r => r.assessmentId === currentAssessmentId
      );
      return assessmentRoles && assessmentRoles.roles.length > 0;
    });
  }, [members, currentAssessmentId]);

  const [selectedMembers, setSelectedMembers] = useState<Set<string>>(new Set());

  // The dialog stays mounted while members load, so it selects everyone on open, not at mount.
  // Seeded on open only: a members refetch while it is open must not undo the user's picks.
  useEffect(() => {
    if (open) setSelectedMembers(new Set(eligibleMembers.map(m => m._id)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const selectAll = useMemo(
    () => eligibleMembers.length > 0 && eligibleMembers.every(m => selectedMembers.has(m._id)),
    [eligibleMembers, selectedMembers]
  );

  const handleToggleAll = () => {
    setSelectedMembers(selectAll ? new Set() : new Set(eligibleMembers.map(m => m._id)));
  };

  const handleToggleMember = (memberId: string) => {
    const newSelected = new Set(selectedMembers);
    if (newSelected.has(memberId)) {
      newSelected.delete(memberId);
    } else {
      newSelected.add(memberId);
    }
    setSelectedMembers(newSelected);
  };

  const handleConfirm = () => {
    onConfirm(Array.from(selectedMembers));
    // Don't call onClose here - let the parent component handle closing after successful creation
  };

  const getRolesDisplay = (member: IMember) => {
    const assessmentRoles = member.rolesByAssessment?.find(
      r => r.assessmentId === currentAssessmentId
    );

    if (!assessmentRoles) return 'No roles';

    const roleCount = assessmentRoles.roles.length;
    if (roleCount === 0) return 'No roles';
    if (roleCount === 1) return `${assessmentRoles.roles[0]}`;
    return `${roleCount} roles`;
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      aria-labelledby="role-assignment-dialog-title"
    >
      <DialogTitle id="role-assignment-dialog-title">
        Copy {sessionTerm} Roles to New {sessionTerm}
      </DialogTitle>

      <DialogContent>
        <Typography variant="body1" gutterBottom>
          Select users to copy their {sessionTerm.toLowerCase()}-level roles from the most recent {sessionTerm.toLowerCase()} to:
        </Typography>
        <Typography variant="body2" color="primary" gutterBottom>
          <strong>{assessmentName}</strong>
        </Typography>

        {eligibleMembers.length === 0 ? (
          <Box mt={2}>
            <Typography variant="body2" color="textSecondary">
              No users with {sessionTerm.toLowerCase()}-level roles found in the most recent {sessionTerm.toLowerCase()}.
            </Typography>
          </Box>
        ) : (
          <>
            <Box mt={2} mb={1}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={selectAll}
                    indeterminate={selectedMembers.size > 0 && selectedMembers.size < eligibleMembers.length}
                    onChange={handleToggleAll}
                    color="primary"
                  />
                }
                label={`Select All (${eligibleMembers.length} users)`}
              />
            </Box>

            <List style={{ maxHeight: 300, overflow: 'auto' }}>
              {eligibleMembers.map((member) => (
                <ListItem
                  key={member._id}
                  button
                  onClick={() => handleToggleMember(member._id)}
                  selected={selectedMembers.has(member._id)}
                >
                  <ListItemText
                    primary={member.user.name}
                    secondary={
                      <>
                        <Typography variant="caption" display="block">
                          {member.user.email}
                        </Typography>
                        <Typography variant="caption" color="primary">
                          {getRolesDisplay(member)}
                        </Typography>
                      </>
                    }
                  />
                  <ListItemSecondaryAction>
                    <IconButton
                      edge="end"
                      onClick={() => handleToggleMember(member._id)}
                    >
                      {selectedMembers.has(member._id) ? (
                        <CheckBoxIcon color="primary" />
                      ) : (
                        <CheckBoxOutlineBlankIcon />
                      )}
                    </IconButton>
                  </ListItemSecondaryAction>
                </ListItem>
              ))}
            </List>
          </>
        )}

        <Box mt={2}>
          <Typography variant="caption" color="textSecondary">
            Note: This will copy the exact roles each selected user has in the most recent {sessionTerm.toLowerCase()}.
            You can refine permissions later in the user management section.
          </Typography>
        </Box>
      </DialogContent>

      <DialogActions>
        <SecondaryButton onClick={onClose}>
          Cancel
        </SecondaryButton>
        <PrimaryButton onClick={handleConfirm}>
          Copy Roles ({selectedMembers.size} users)
        </PrimaryButton>
      </DialogActions>
    </Dialog>
  );
};
